import { DatePicker as InnerDatePicker, FormControl, FormHelperText, FormLabel } from '@ellucian/react-design-system/core';
import { withStyles } from '@ellucian/react-design-system/core/styles';
import classnames from 'classnames';
import moment = require('moment');
import React = require('react');

type FieldChangedFunc<T> = (
    event: { target: { name: string, value: T } }
) => void;

import {
    colorTextAlertError,
    colorTextDisabled,
    colorTextPrimary,
    fontFamilyDefault,
    fontSizeLarge,
    fontWeightNormal,
    heightDropdownLabel,
    widthInputFields
} from '@ellucian/react-design-system/core/styles/tokens';

const styles = (theme) => ({
    '@global': {
        '.DateInput': {
            height: '52px',
            width: 'auto',
        },
        '.DateInput_input': {
            'color': colorTextPrimary,
            'font-family': fontFamilyDefault,
            'font-size': fontSizeLarge,
            'font-weight': fontWeightNormal,
            'height': '52px',
            'line-height': heightDropdownLabel,
            'padding': '0 13px',
        },
        '.DateInput_input__disabled': {
            'color': colorTextDisabled,
            'font-style': 'normal'
        },
        '.SingleDatePicker': {
            width: '100%'
        },
        '.SingleDatePickerInput': {
            width: ['100%', '!important'],
        },
        '.SingleDatePickerInput_calendarIcon': {
            paddingTop: '13px',
        },
        '.SingleDatePicker_picker': {
            'z-index': ['1460', '!important']
        },
    },
    'error': {
        color: colorTextAlertError
    },
    'formControl': {
        width: widthInputFields
    },
    'formControlFullWidth': {
        width: '100%'
    },
    'labelClass': {
        display: 'block',
        fontSize: theme.typography.caption.fontSize,
        fontWeight: fontWeightNormal,
        lineHeight: 'normal'
    }
} as any);

export interface IDatePickerProps {
    'data-testid'?: string;
    date?: moment.Moment;
    className?: any;
    classes?: any;
    isOutsideRange?: (day: moment.Moment) => boolean;
    onDateChange?: (it: moment.Moment) => void;
    placeholder?: string;
    helperText?: string;
    name?: string;
    error?: boolean;
    fullWidth?: boolean;
    label?: string;
    required?: boolean;
    id?: string;
    readOnly?: boolean;
    disabled?: boolean;
    screenReaderInputMessage?: string;
    isRTL?: boolean;
    enableOutsideDays?: boolean;
    anchorDirection?: string;
    displayFormat?: any;
    monthFormat?: (month: any) => string;
    renderMonthText?: (month: any) => string;
    renderDayContents?: (day: any) => string;
    onFocusChange?: (focused: boolean) => void;
    autoPosition?: boolean;

    // added properties
    nativeDate?: Date;
    onNativeDateChange?: FieldChangedFunc<Date>;
}

interface IState {
    focused: boolean;
}

export class DatePickerInternal extends React.Component<IDatePickerProps, IState> {
    constructor(props: IDatePickerProps) {
        super(props);

        this.state = {
            focused: false,
        };
    }
    public render() {
        const {
            focused
        } = this.state;

        const {
            classes = {},
            className,
            'data-testid': testId,
            error,
            fullWidth,
            label,
            required,
            onDateChange,
            helperText,
            name,
            date,
            nativeDate,
            onFocusChange,
            onNativeDateChange,
            autoPosition,
            ...rest
        } = this.props;

        const d = date || (nativeDate && moment(nativeDate));

        const classNames = classnames(classes.formControl, className, {
            [classes.formControlFullWidth]: !!fullWidth,
        });

        return (
            <FormControl className={classNames} data-testid={testId} error={error}>
                <FormLabel required={required} className={classes.labelClass}>
                    {label}
                </FormLabel>
                <InnerDatePicker
                    {...rest}
                    focused={focused}
                    date={d}
                    onDateChange={this.onDateChange}
                    onFocusChange={this.onFocusChange}
                    autoPosition={!!autoPosition}
                />
                {!!error &&
                    <FormHelperText>
                        {helperText}
                    </FormHelperText>
                }
            </FormControl>
        );
    }

    public onDateChange = (v?: Date) => {
        const value = v || null;
        if (this.props.onNativeDateChange) {
            this.props.onNativeDateChange({
                target: {
                    name: this.props.name,
                    value,
                }
            });
        }
    }

    private onFocusChange = ({ focused }: any) => {
        this.setState({ focused }, () => {
            if (this.props.onFocusChange) {
                this.props.onFocusChange(focused);
            }
        });
    }
}

export const DatePicker = [
    withStyles(styles)
].reduce((memo, hoc: (it: any) => any) => hoc(memo), DatePickerInternal);

export default DatePicker;
